import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { useLang, useL, pick } from "@/lib/i18n";
import { api, errDetail } from "@/lib/api";
import { StatusBadge } from "@/components/member/Lists";
import { F, Card, RedBtn } from "./ui";
import MemberDialog from "./MemberDialog";

function AddMember({ onDone }) {
  const L = useL();
  const { tErr } = useLang();
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ name: "", email: "", phone: "" });
  const save = async (e) => {
    e.preventDefault();
    try { await api.post("/admin/members", f); toast.success("OK"); setOpen(false); setF({ name: "", email: "", phone: "" }); onDone(); }
    catch (ex) { toast.error(tErr(errDetail(ex))); }
  };
  return (
    <>
      <RedBtn onClick={() => setOpen(true)} data-testid="admin-add-member-btn" className="inline-flex items-center gap-1.5"><Plus size={16} />{L("Ajouter un membre", "Add member")}</RedBtn>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="border-white/10 bg-[#0f1015]" aria-describedby={undefined}>
          <DialogHeader><DialogTitle>{L("Nouveau membre", "New member")}</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-3">
            <F id="add-member-name" label={L("Nom", "Name")} required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
            <F id="add-member-email" label="Email" type="email" required value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} />
            <F id="add-member-phone" label={L("Téléphone", "Phone")} value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} />
            <p className="text-xs text-slate-500">{L("Le membre définira son mot de passe via « Mot de passe oublié ».", "The member sets their password via “Forgot password”.")}</p>
            <RedBtn type="submit" data-testid="add-member-submit">{L("Créer", "Create")}</RedBtn>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default function AdminMembers({ plans }) {
  const { lang } = useLang();
  const L = useL();
  const [q, setQ] = useState("");
  const [list, setList] = useState([]);
  const [sel, setSel] = useState(null);
  const load = useCallback(() => api.get("/admin/members", { params: { q } }).then((r) => setList(r.data)), [q]);
  useEffect(() => { const h = setTimeout(load, 250); return () => clearTimeout(h); }, [load]);
  const planName = (id) => { const p = plans.find((x) => x.id === id); return p ? pick(p, "name", lang) : "—"; };

  return (
    <Card title={L("Membres", "Members")} right={<AddMember onDone={load} />} testId="admin-members">
      <Input placeholder={L("Rechercher nom, courriel, carte…", "Search name, email, keycard…")} value={q} onChange={(e) => setQ(e.target.value)} className="mb-4 border-white/15 bg-black/40" data-testid="admin-member-search" />
      <div className="overflow-x-auto">
        <table className="w-full min-w-[640px] text-sm">
          <thead><tr className="border-b border-white/10 text-left font-mono text-[11px] uppercase tracking-widest text-slate-500">
            <th className="py-2">{L("Membre", "Member")}</th><th>{L("Statut", "Status")}</th><th>{L("Forfait", "Plan")}</th><th>{L("Heures", "Hours")}</th><th>{L("Carte", "Keycard")}</th>
          </tr></thead>
          <tbody>
            {list.map((m) => (
              <tr key={m.id} onClick={() => setSel(m.id)} className="cursor-pointer border-b border-white/5 hover:bg-white/5" data-testid={`admin-member-row-${m.id}`}>
                <td className="py-3"><p className="text-white">{m.name}</p><p className="text-xs text-slate-500">{m.email}</p></td>
                <td><StatusBadge s={m.membership_status} /></td>
                <td className="text-slate-300">{planName(m.plan_id)}</td>
                <td className="font-mono text-white">{Number(m.hours_balance || 0).toFixed(1)}</td>
                <td className="text-slate-300">{m.keycard?.number || "—"} <StatusBadge s={m.keycard?.status || "unassigned"} /></td>
              </tr>
            ))}
          </tbody>
        </table>
        {!list.length && <p className="py-6 text-center text-sm text-slate-500">{L("Aucun membre.", "No members.")}</p>}
      </div>
      {sel && <MemberDialog id={sel} plans={plans} onClose={() => { setSel(null); load(); }} />}
    </Card>
  );
}
